import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Wifi, CheckCircle2, Loader } from 'lucide-react';
import api from '../services/api';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [step, setStep] = useState('welcome'); // welcome, creating, success
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Verificar si ya tiene credenciales VPN inicializadas
    checkVPNStatus();
  }, []);

  const checkVPNStatus = async () => {
    try {
      const response = await api.get('/api/licenses/my-routers');
      const routers = response.data.routers || [];
      
      // Si ya tiene routers con credenciales, redirigir al dashboard
      if (routers.length > 0 && routers[0].vpn_username) {
        console.log('✅ Credenciales VPN ya inicializadas, redirigiendo...');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Error verificando estado VPN:', error);
    }
  };

  const handleInitialize = async () => {
    setStep('creating');
    setError(null);
    setProgress(0);

    try {
      // Paso 1: Conectando al servidor
      setProgress(20);
      console.log('🔑 Iniciando creación de credenciales VPN...');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Paso 2: Generando credenciales
      setProgress(40);
      const response = await api.post('/api/licenses/initialize-vpn');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Paso 3: Asignando recursos
      setProgress(70);
      console.log('✅ Credenciales creadas:', response.data);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Guardar en localStorage
      localStorage.setItem('vpn_initialized', 'true');
      
      setProgress(90);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mostrar estado de éxito
      setStep('success');
      setProgress(100);
      
      // Esperar 2.5 segundos y redirigir al dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2500);
      
    } catch (error) {
      console.error('❌ Error inicializando VPN:', error);
      setError(error.response?.data?.detail || 'Error al crear credenciales. Por favor intenta nuevamente.');
      setStep('welcome');
      setProgress(0);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl p-8 md:p-12 ${
        isDark ? 'bg-gray-800/90 backdrop-blur' : 'bg-white/90 backdrop-blur'
      }`}>
        
        {/* ESTADO 1: BIENVENIDA */}
        {step === 'welcome' && (
          <div className="text-center">
            {/* Icono animado */}
            <div className="mb-8 flex justify-center">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                isDark ? 'bg-purple-900/50' : 'bg-purple-100'
              } animate-pulse`}>
                <Wifi className={`w-12 h-12 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>

            {/* Título */}
            <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              ¡Bienvenido a EasyTickets!
            </h1>
            
            <p className={`text-lg mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Prepara tu espacio de trabajo
            </p>

            {/* Qué incluye */}
            <div className={`rounded-xl p-6 mb-8 text-left ${
              isDark ? 'bg-blue-900/30 border-2 border-blue-700' : 'bg-blue-50 border-2 border-blue-200'
            }`}>
              <h3 className={`font-semibold text-lg mb-4 ${
                isDark ? 'text-blue-300' : 'text-blue-900'
              }`}>
                ¿Qué vamos a configurar?
              </h3>
              <ul className={`space-y-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl">•</span>
                  <span>Credenciales VPN únicas</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl">•</span>
                  <span>IP interna y puerto externo</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 text-xl">•</span>
                  <span>Script de configuración para Mikrotik</span>
                </li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 bg-red-100 border-2 border-red-300 rounded-lg text-red-800 text-sm">
                ⚠️ {error}
              </div>
            )}

            {/* Botón */}
            <button
              onClick={handleInitialize}
              className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              ✨ Inicializar mi espacio de trabajo
            </button>

            <p className={`text-xs mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Esto solo toma unos segundos
            </p>
          </div>
        )}

        {/* ESTADO 2: CREANDO */}
        {step === 'creating' && (
          <div className="text-center">
            {/* Spinner */}
            <div className="mb-8 flex justify-center">
              <Loader className={`w-16 h-16 animate-spin ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>

            {/* Título */}
            <h2 className={`text-2xl md:text-3xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              Creando tus credenciales VPN...
            </h2>

            {/* Pasos con animación */}
            <div className={`space-y-4 mb-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex items-center gap-3 justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>Conectando al servidor VPN</span>
              </div>
              <div className="flex items-center gap-3 justify-center">
                <div className={`w-5 h-5 rounded-full border-2 ${
                  progress >= 40 ? 'bg-green-500 border-green-500' : 'border-gray-400'
                } flex items-center justify-center`}>
                  {progress >= 40 && <span className="text-white text-xs">✓</span>}
                </div>
                <span>Generando credenciales únicas</span>
              </div>
              <div className="flex items-center gap-3 justify-center">
                <div className={`w-5 h-5 rounded-full border-2 ${
                  progress >= 70 ? 'bg-green-500 border-green-500' : 'border-gray-400'
                } flex items-center justify-center`}>
                  {progress >= 70 && <span className="text-white text-xs">✓</span>}
                </div>
                <span>Asignando IP y puerto</span>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p className={`text-sm mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Esto puede tomar unos segundos...
            </p>
          </div>
        )}

        {/* ESTADO 3: ÉXITO */}
        {step === 'success' && (
          <div className="text-center">
            {/* Checkmark animado */}
            <div className="mb-8 flex justify-center">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center animate-bounce">
                <CheckCircle2 className="w-16 h-16 text-green-600" />
              </div>
            </div>

            {/* Título */}
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              ¡Todo listo!
            </h2>
            
            <p className={`text-lg mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Tu espacio de trabajo está configurado
            </p>

            {/* Barra de progreso automática */}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-4">
              <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 w-full animate-pulse" />
            </div>

            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Redirigiendo al dashboard...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
